/**
 * ADR-206 — the files half of the plugin postMessage bridge.
 *
 * Kept out of PluginPage.tsx so it can be tested without React: the page hands
 * every message here first, and anything this does not recognise falls through
 * to the existing data/call handlers.
 *
 *   plugin.files.request  {requestId, scope, op, args}   -> plugin.files.response
 *   plugin.files.upload   {requestId, scope, path, files} -> plugin.files.progress… then plugin.files.response
 *   plugin.files.cancel   {requestId}                     -> (aborts that upload)
 *   plugin.files.download {requestId, scope, path}        -> plugin.files.response
 *   plugin.files.bytes    {requestId, scope, path}        -> plugin.files.response {bytes} (transferred)
 *   plugin.files.copy_link {requestId, scope, path}       -> plugin.files.response {copied, url, reason?}   (ADR-210)
 *
 * ADR-210: `plugin.files.request` with op `share_link` answers `{data:{url}}`, the ABSOLUTE
 * permanent link (`https://<mc-host>/files/<scope>/<path>`). `copy_link` writes that link to
 * the clipboard from HERE (the sandboxed iframe has no clipboard permission) and says whether
 * it worked, so the plugin can show the link in a selectable box when it did not.
 *
 * The scope is a DECLARED scope id; Mission Control re-checks it server-side,
 * so nothing here is a security boundary — it is plumbing with good manners.
 */
import { downloadFile, fileBytes, filesOp, uploadFile, type FilesResult } from '../api/pluginFiles'
import { absoluteLink, copyToClipboard } from '../api/fileLinks'
import { isValidSharePath, permanentFilePath } from '../utils/fileLink'

export interface FilesBridgeContext {
  pluginId: string
  /** Post to the iframe. `transfer` moves an ArrayBuffer instead of copying it. */
  post: (msg: Record<string, unknown>, transfer?: Transferable[]) => void
  /** In-flight uploads, by requestId, so a cancel can find them. */
  uploads: Map<string, AbortController>
  /** Ids of the files scopes the plugin declares (ADR-210 copy_link checks against them). */
  fileScopes?: string[]
}

const isStr = (v: unknown): v is string => typeof v === 'string'

function respond(ctx: FilesBridgeContext, requestId: string, r: FilesResult, extra: Record<string, unknown> = {}, transfer?: Transferable[]) {
  ctx.post({ type: 'plugin.files.response', requestId, ok: r.ok, status: r.status, data: r.data, error: r.error, message: r.message, ...extra }, transfer)
}

/** Returns true when the message was a files message (handled or rejected). */
export function handleFilesMessage(msg: Record<string, unknown>, ctx: FilesBridgeContext): boolean {
  const type = msg.type
  if (!isStr(type) || !type.startsWith('plugin.files.')) return false
  const { requestId, scope } = msg
  if (!isStr(requestId)) return true

  switch (type) {
    case 'plugin.files.request': {
      if (!isStr(scope) || !isStr(msg.op)) return true
      const args = msg.args && typeof msg.args === 'object' ? (msg.args as Record<string, unknown>) : {}
      void filesOp(ctx.pluginId, scope, msg.op, args).then((r) => {
        if (msg.op === 'share_link' && r.ok) {
          // The server answers a site-relative path; only the host knows its own origin.
          const rel = (r.data as { url?: string } | undefined)?.url
          if (typeof rel === 'string') r = { ...r, data: { url: absoluteLink(rel) } }
        }
        respond(ctx, requestId, r)
      })
      return true
    }
    case 'plugin.files.upload': {
      if (!isStr(scope)) return true
      const dir = isStr(msg.path) ? msg.path : ''
      const files = Array.isArray(msg.files) ? msg.files.filter((f): f is File => f instanceof File) : []
      if (files.length === 0) {
        respond(ctx, requestId, { ok: false, status: 400, error: 'no_files', message: 'No files were chosen.' })
        return true
      }
      const controller = new AbortController()
      ctx.uploads.set(requestId, controller)
      void (async () => {
        const results: Array<{ name: string } & FilesResult> = []
        for (let i = 0; i < files.length; i++) {
          const r = await uploadFile(ctx.pluginId, scope, dir, files[i], i,
            (p) => ctx.post({ type: 'plugin.files.progress', requestId, ...p }),
            controller.signal)
          results.push({ name: files[i].name, ...r })
        }
        ctx.uploads.delete(requestId)
        const failed = results.filter((r) => !r.ok)
        respond(ctx, requestId, {
          ok: failed.length === 0,
          status: failed.length === 0 ? 200 : failed[0].status,
          message: failed.length === 0 ? undefined : failed.map((f) => f.message).join(' '),
        }, { results: results.map((r) => ({ name: r.name, ok: r.ok, message: r.message })) })
      })()
      return true
    }
    case 'plugin.files.cancel': {
      ctx.uploads.get(requestId)?.abort()
      return true
    }
    case 'plugin.files.download': {
      if (!isStr(scope) || !isStr(msg.path)) return true
      void downloadFile(ctx.pluginId, scope, msg.path).then((r) => respond(ctx, requestId, r))
      return true
    }
    case 'plugin.files.bytes': {
      if (!isStr(scope) || !isStr(msg.path)) return true
      void fileBytes(ctx.pluginId, scope, msg.path).then((r) => {
        const { bytes, ...rest } = r
        respond(ctx, requestId, rest, bytes ? { bytes } : {}, bytes ? [bytes] : undefined)
      })
      return true
    }
    case 'plugin.files.copy_link': {
      if (!isStr(scope) || !isStr(msg.path)) return true
      const fail = (error: string, message: string) =>
        respond(ctx, requestId, { ok: false, status: 400, error, message }, { copied: false, reason: error })
      if (!ctx.fileScopes || !ctx.fileScopes.includes(scope)) {
        fail('scope_not_declared', 'This plugin has no such file area.')
        return true
      }
      if (!isValidSharePath(msg.path)) {
        fail('invalid_path', 'Invalid location.')
        return true
      }
      // Built here, not fetched: the clipboard write must happen inside the click's user
      // activation, which a network round trip can lose (Safari). Clipboard is called
      // synchronously from this message handler.
      const url = absoluteLink(permanentFilePath(scope, msg.path))
      void copyToClipboard(url).then((c) =>
        respond(ctx, requestId, { ok: true, status: 200 }, { copied: c.copied, url: c.url, ...(c.reason ? { reason: c.reason } : {}) }))
      return true
    }
    default:
      return true
  }
}
